import React, { useEffect } from "react";
import { useAppSelector } from "../../../redux/store";
import { formSliceSelectors } from "../../../redux/forms/form.selector";
import { SpinnerFullScreen } from "../../../components/SpinnerFullScreen";
import { AtiraEmpty } from "../../../components/AtiraEmpty";
import { useTranslation } from "react-i18next";
import { thresholdSliceSelectors } from "../../../redux/threshold/threshold.selector";
import { Form } from "./Form";
import { useAtiraFormContext } from "../context/form-context";
import { canCreateForm } from "../utils/FormUtils";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { useLocation, useParams } from "react-router-dom";
import last from "lodash/last";
import isEmpty from "lodash/isEmpty";
import { Flex } from "../../../components/Flex";
import styled from "styled-components";
import { Spacing } from "../../../theme/Spacing";
import { useFormContext } from "react-hook-form";
import { FormType } from "../../../model/form/types/FormType.enum";

const FormsWrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: ${Spacing.m};
  padding: ${Spacing.m};
  padding-top: 0;
`;

export const FormsContent: React.FC = () => {
  const { t } = useTranslation();
  const { folderSlug } = useParams<{ folderSlug: string }>();
  const { pathname } = useLocation();
  const {
    setFormCreateEditDrawerVisible,
    highlightedFormId,
    setHighlightedFormId,
  } = useAtiraFormContext();
  const { watch } = useFormContext<{ type: FormType | null }>();
  const filterType = watch("type");

  const folderId = last(folderSlug?.split("-"));

  const parentFolder = useAppSelector((s) =>
    formSliceSelectors.selectFolderById(s, folderId || ""),
  );
  const formsLoading = useAppSelector(formSliceSelectors.selectFormsLoading);
  const allForms = useAppSelector(formSliceSelectors.selectForms)?.filter(
    (f) => f.orphan !== true,
  );
  const thresholds = useAppSelector(
    thresholdSliceSelectors.selectThresholdStats,
  );

  const isOnFolderFormsPage = pathname.includes("/forms/folder-");

  const forms =
    isOnFolderFormsPage && !isEmpty(parentFolder)
      ? parentFolder?.forms
      : allForms;
  const filteredForms =
    forms?.filter((f) => (filterType ? f.type === filterType : true)) || [];

  const userPermissions = useAppSelector(userSliceSelectors.selectLoggedInUser)
    ?.permissions.permissions;

  const isMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);

  const reachedFormsLimit = !canCreateForm(thresholds);

  useEffect(() => {
    if (!highlightedFormId) {
      return;
    }

    const highlightedFormVisible = filteredForms.some(
      (form) => form._id === highlightedFormId,
    );

    if (!highlightedFormVisible) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setHighlightedFormId(null);
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [filteredForms, highlightedFormId, setHighlightedFormId]);

  if (formsLoading && !forms?.length) {
    return <SpinnerFullScreen containerHeight={"10rem"} />;
  }

  if (!forms?.length) {
    return (
      <AtiraEmpty
        action={
          !isOnFolderFormsPage
            ? {
                title: t("common.create"),
                onClick: userPermissions?.FORM.CREATE
                  ? () => setFormCreateEditDrawerVisible(true)
                  : undefined,
                disabled: reachedFormsLimit,
                tooltip: reachedFormsLimit
                  ? t("forms.reachedLimit", {
                      count: thresholds?.total?.FORMS,
                    })
                  : undefined,
              }
            : undefined
        }
        description={
          isMember ? t("forms.no_forms_yet.member") : t("forms.no_forms_yet")
        }
      />
    );
  }

  return (
    <FormsWrapper>
      {filteredForms.map((f) => (
        <Form key={f._id} form={f} highlighted={f._id === highlightedFormId} />
      ))}
    </FormsWrapper>
  );
};
